import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { query, where, getCountFromServer, getAggregateFromServer, sum, getDocs, Timestamp } from 'firebase/firestore';
import * as XLSX from 'xlsx';

import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import { Header } from '../../../../components/Header/Header';
import { Title } from '../../../../components/Title/Title';
import OrderGrid from '../../../../components/OrderGrid ';

import { citiesRef, completedOrdersRef } from '../../../../utils/collectionRefs';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { Button, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Stack, } from '@mui/material';
import SearchInput from '../../../../components/SearchInput/SearchInput';

const DailyReport = ({ pageTitle }) => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month'));;
  const [searchTerm, setSearchTerm] = useState('');

  const getCityStats = async (cityId, cityName, start, end) => {
    try {
      const cityQuery = query(completedOrdersRef,
        where('cityTo', '==', cityId),
        where('dateDelivered', '>=', Timestamp.fromDate(start.toDate())),
        where('dateDelivered', '<=', Timestamp.fromDate(end.toDate()))
      );

      const countSnapshot = await getCountFromServer(cityQuery);
      const sumSnapshot = await getAggregateFromServer(cityQuery, {
        total: sum('cost'),
      });

      return {
        id: cityId,
        city: cityName,
        count: countSnapshot.data().count || 0,
        total: sumSnapshot.data().total || 0,
        bezNal: sumSnapshot.data().total || 0,
        nalichka: 0,
      };
    } catch (error) {
      console.error(`Ошибка при загрузке статистики для города ${cityName}:`, error);
      return { id: cityId, city: cityName, count: 0, total: 0, bezNal: 0, nalichka: 0 };
    }
  };

  const fetchCitiesAndStats = async (start, end) => {
    try {
      setIsLoading(true);
      const citiesSnapshot = await getDocs(citiesRef);
      const citiesData = citiesSnapshot?.docs?.map(doc => doc?.data());

      const stats = await Promise.all(
        citiesData.map(city => getCityStats(city?.id, city?.name, start, end))
      );

      setOrders(stats);
    } catch (error) {
      console.error('Ошибка при загрузке статистики:', error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchCitiesAndStats(startDate, endDate);
  }, [startDate, endDate]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredOrders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Отчет');
    XLSX.writeFile(
      workbook,
      `Отчет-${startDate.format('YYYY-MM-DD')}--${endDate.format('YYYY-MM-DD')}.xlsx`
    );
  };

  const filteredOrders = orders?.filter(order =>
    order.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { totalOrdersSum, totalOrdersCount } = filteredOrders?.reduce(
    (acc, order) => {
      acc.totalOrdersSum += order.total;
      acc.totalOrdersCount += order.count;
      return acc;
    },
    { totalOrdersSum: 0, totalOrdersCount: 0 }
  );

  return (
    <div className='containerr'>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Header previous='' initial={pageTitle} />
      <Title title={pageTitle} icon={<SourceOutlinedIcon fontSize='24' />} />
      <div className="container-inner">
        <div className="orders-sorting-header">
          <Stack
            sx={{ width: '100%', justifyContent: 'flex-start', flexWrap: 'wrap' }}
            direction='row'
            gap={2}
          >
            <div>
              <Button variant="black-contained" onClick={exportToExcel} sx={{ margin: '0' }}>
                Экспорт в Excel
              </Button>
            </div>
            <SearchInput
              label="Поиск по городу"
              placeholder="Введите название"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
            />
            <DatePicker
              label="Дата начала"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD.MM.YYYY"
              sx={{
                width: '180px',
                '& .MuiInputBase-root': { height: '40px' },
              }}
            />
            <DatePicker
              label="Дата конца"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD.MM.YYYY"
              sx={{
                width: '180px',
                '& .MuiInputBase-root': { height: '40px' },
              }}
            />

          </Stack>
        </div>
        <TableContainer component={Paper} sx={{ marginTop: 2 }}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#ECECEC' }}>
                <TableCell>Итого</TableCell>
                <TableCell>Общая стоимость</TableCell>
                <TableCell>Безнал</TableCell>
                <TableCell>Наличка</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>{totalOrdersCount.toLocaleString()}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalOrdersSum.toLocaleString()}⊆</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalOrdersSum.toLocaleString()}⊆</strong>
                </TableCell>
                <TableCell>
                  <strong>0⊆</strong>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={5} style={{ padding: 0 }}>
                  <OrderGrid
                    orders={filteredOrders}
                    loading={isLoading}
                    columns={[
                      { field: 'city', headerName: 'Услуги (Город)', flex: 1 },
                      { field: 'count', headerName: 'Кол-во заказов', flex: 1 },
                      { field: 'total', headerName: 'Общая стоимость', flex: 1 },
                      { field: 'bezNal', headerName: 'Безнал', flex: 1 },
                      { field: 'nalichka', headerName: 'Наличка', flex: 1 },
                    ]}
                    size={40}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}

export default DailyReport